import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingsApi, reviewsApi, disputesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Calendar, MapPin, Star, Loader2, AlertTriangle } from 'lucide-react';
import { formatDate, formatCurrency, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';

const defaultImage = 'https://images.unsplash.com/photo-1563116128-261f46267cac?w=400';

export default function Trips() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  
  const [reviewDialog, setReviewDialog] = useState({ open: false, booking: null });
  const [disputeDialog, setDisputeDialog] = useState({ open: false, booking: null });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [disputeForm, setDisputeForm] = useState({ reason: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await bookingsApi.getAll();
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = (status) => {
    const now = new Date();
    return bookings.filter((booking) => {
      const checkOut = new Date(booking.check_out);
      if (status === 'upcoming') {
        return ['pending', 'accepted'].includes(booking.status) && checkOut >= now;
      } else if (status === 'past') {
        return booking.status === 'completed' || checkOut < now;
      } else if (status === 'cancelled') {
        return ['cancelled', 'declined'].includes(booking.status);
      }
      return false;
    });
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await bookingsApi.updateStatus(bookingId, 'cancelled');
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const handleSubmitReview = async () => {
    setSubmitting(true);
    try {
      await reviewsApi.create({
        booking_id: reviewDialog.booking.id,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      });
      toast.success('Review submitted!');
      setReviewDialog({ open: false, booking: null });
      setReviewForm({ rating: 5, comment: '' });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitDispute = async () => {
    setSubmitting(true);
    try {
      await disputesApi.create({
        booking_id: disputeDialog.booking.id,
        reason: disputeForm.reason,
        description: disputeForm.description,
      });
      toast.success('Dispute opened');
      setDisputeDialog({ open: false, booking: null });
      setDisputeForm({ reason: '', description: '' });
    } catch (error) {
      toast.error('Failed to open dispute');
    } finally {
      setSubmitting(false);
    }
  };

  const BookingCard = ({ booking }) => {
    const listing = booking.listing || {};
    const isPast = booking.status === 'completed' || new Date(booking.check_out) < new Date();

    return (
      <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md overflow-hidden card-hover" data-testid={`booking-card-${booking.id}`}>
        <div className="flex flex-col md:flex-row">
          <div className="md:w-48 h-40 md:h-auto">
            <img
              src={listing.photos?.[0] || defaultImage}
              alt={listing.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = defaultImage; }}
            />
          </div>
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 
                  className="font-serif text-lg font-semibold text-farmhouse-cream hover:text-farmhouse-saddle cursor-pointer transition-colors"
                  onClick={() => navigate(`/listings/${booking.listing_id}`)}
                >
                  {listing.title || 'Listing'}
                </h3>
                <Badge className={`mt-1 ${getStatusColor(booking.status)} border-0 capitalize`}>
                  {booking.status}
                </Badge>
              </div>
              <p className="text-xl font-serif font-semibold text-farmhouse-cream">
                {formatCurrency(booking.total_amount)}
              </p>
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-farmhouse-muted mt-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(booking.check_in)} - {formatDate(booking.check_out)}</span>
              </div>
              <div className="flex items-center gap-1">
                <span>{booking.num_animals} animal{booking.num_animals > 1 ? 's' : ''}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {booking.status === 'pending' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCancelBooking(booking.id)}
                  className="text-farmhouse-clay border-farmhouse-clay hover:bg-farmhouse-clay/10"
                  data-testid="cancel-booking"
                >
                  Cancel Request
                </Button>
              )}
              {booking.status === 'accepted' && !isPast && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCancelBooking(booking.id)}
                  className="text-farmhouse-clay border-farmhouse-clay hover:bg-farmhouse-clay/10"
                >
                  Cancel Booking
                </Button>
              )}
              {booking.status === 'completed' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => setReviewDialog({ open: true, booking })}
                    className="btn-primary"
                    data-testid="leave-review"
                  >
                    <Star className="w-4 h-4 mr-1" />
                    Leave Review
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDisputeDialog({ open: true, booking })}
                    className="text-farmhouse-clay border-farmhouse-subtle hover:bg-farmhouse-subtle"
                    data-testid="open-dispute"
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Report Issue
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="trips-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-8">
          My Trips
        </h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-farmhouse-paper border border-farmhouse-subtle mb-6">
            <TabsTrigger 
              value="upcoming"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Upcoming ({filterBookings('upcoming').length})
            </TabsTrigger>
            <TabsTrigger 
              value="past"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Past ({filterBookings('past').length})
            </TabsTrigger>
            <TabsTrigger 
              value="cancelled"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Cancelled ({filterBookings('cancelled').length})
            </TabsTrigger>
          </TabsList>

          {['upcoming', 'past', 'cancelled'].map((tab) => (
            <TabsContent key={tab} value={tab}>
              {filterBookings(tab).length > 0 ? (
                <div className="space-y-4">
                  {filterBookings(tab).map((booking) => (
                    <BookingCard key={booking.id} booking={booking} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-farmhouse-paper border border-farmhouse-subtle rounded-md">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-farmhouse-subtle" />
                  <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-2">
                    No {tab} trips
                  </h3>
                  <p className="text-farmhouse-muted mb-4">
                    {tab === 'upcoming' ? 'Time to plan your next adventure!' : `You don't have any ${tab} bookings.`}
                  </p>
                  {tab === 'upcoming' && (
                    <Button onClick={() => navigate('/search')} className="btn-primary">
                      Browse Listings
                    </Button>
                  )}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* Review Dialog */}
        <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog({ ...reviewDialog, open })}>
          <DialogContent className="bg-farmhouse-paper border-farmhouse-subtle">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-farmhouse-cream">
                Leave a Review
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-farmhouse-cream mb-2 block">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                      className="p-1"
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          star <= reviewForm.rating ? 'text-farmhouse-gold fill-current' : 'text-farmhouse-subtle'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-farmhouse-cream mb-2 block">Your Review</label>
                <Textarea
                  placeholder="Share your experience..."
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  className="form-input min-h-[120px]"
                />
              </div>
              <Button
                onClick={handleSubmitReview}
                className="btn-primary w-full"
                disabled={submitting || !reviewForm.comment}
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit Review'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dispute Dialog */}
        <Dialog open={disputeDialog.open} onOpenChange={(open) => setDisputeDialog({ ...disputeDialog, open })}>
          <DialogContent className="bg-farmhouse-paper border-farmhouse-subtle">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-farmhouse-cream">
                Report an Issue
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-farmhouse-cream mb-2 block">Reason</label>
                <input
                  type="text"
                  placeholder="Brief reason for dispute"
                  value={disputeForm.reason}
                  onChange={(e) => setDisputeForm({ ...disputeForm, reason: e.target.value })}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label className="text-farmhouse-cream mb-2 block">Description</label>
                <Textarea
                  placeholder="Describe the issue in detail..."
                  value={disputeForm.description}
                  onChange={(e) => setDisputeForm({ ...disputeForm, description: e.target.value })}
                  className="form-input min-h-[120px]"
                />
              </div>
              <Button
                onClick={handleSubmitDispute}
                className="btn-primary w-full"
                disabled={submitting || !disputeForm.reason || !disputeForm.description}
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit Dispute'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
